import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useRef, useState } from "react";
import { InfoRow } from "./functions";
import { formatJurisdictionLabel, formatPercent } from "../../utils/companiesFormatting";
import {
  formatAsOfDate,
  formatMiningMethod,
  getResourceAsOfDate,
  getResourceLastUpdatedDate,
  getResourceSourceReport,
} from "./assetResourceDetailUtils";

const DASH = "-";
const DRILL_INTERCEPT_PREVIEW_LENGTH = 75;

const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === "") return DASH;
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: Number.isInteger(parsed) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(parsed);
};

const getCoordinatePair = (data) => {
  if (
    data?.latitude === null ||
    data?.latitude === undefined ||
    data?.latitude === "" ||
    data?.longitude === null ||
    data?.longitude === undefined ||
    data?.longitude === ""
  ) {
    return null;
  }
  const latitude = Number(data?.latitude);
  const longitude = Number(data?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return { latitude, longitude };
};

const formatOwnership = (data) => {
  const percentage = formatPercent(data.ownership_percentage, 2);
  const ownershipType = data.ownership_type || "Owner/Operator";
  return [percentage, ownershipType].filter(Boolean).join(" ") || DASH;
};

const getGoogleMapsUrl = (data) => {
  const coordinates = getCoordinatePair(data);
  if (!coordinates) return null;
  return `https://www.google.com/maps/search/?api=1&query=${coordinates.latitude},${coordinates.longitude}`;
};

const getLifecycleLabel = (data) => data?.lifecycle || data?.lifecycle_code || DASH;

const formatDrillResultDate = (value) => {
  if (!value) return DASH;
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(...value.split("-").map((part, index) => Number(part) - (index === 1 ? 1 : 0)))
    : new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
};

function DrillResultSummary({ result }) {
  const [expanded, setExpanded] = useState(false);
  const intercept = result?.intercept_raw || DASH;
  const shouldTruncate = intercept.length > DRILL_INTERCEPT_PREVIEW_LENGTH;
  const displayIntercept =
    shouldTruncate && !expanded
      ? `${intercept.slice(0, DRILL_INTERCEPT_PREVIEW_LENGTH)}...`
      : intercept;

  return (
    <Box
      sx={{
        py: 1.25,
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Typography
        component="div"
        variant="body2"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {displayIntercept}
      </Typography>
      {(shouldTruncate || result?.result_date || result?.source_url) ? (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
          {shouldTruncate ? (
            <Button
              variant="text"
              size="small"
              onClick={() => setExpanded((current) => !current)}
              sx={{ minWidth: 0, p: 0, fontWeight: 700, textTransform: "none" }}
            >
              {expanded ? "Show less" : "Show more"}
            </Button>
          ) : null}
          {shouldTruncate && (result?.result_date || result?.source_url) ? (
            <Typography variant="caption" color="text.secondary">
              ·
            </Typography>
          ) : null}
          {result?.result_date ? (
            <Typography variant="caption" color="text.secondary">
              {formatDrillResultDate(result.result_date)}
            </Typography>
          ) : null}
          {result?.result_date && result?.source_url ? (
            <Typography variant="caption" color="text.secondary">
              ·
            </Typography>
          ) : null}
          {result?.source_url ? (
            <Button
              href={result.source_url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="text"
              endIcon={<OpenInNewIcon fontSize="inherit" />}
              sx={{ minWidth: 0, p: 0, fontWeight: 700, textTransform: "none" }}
            >
              Source
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}

const createCircleMarkerElement = (color) => {
  const element = document.createElement("button");
  element.type = "button";
  element.setAttribute("aria-label", "Asset location");
  Object.assign(element.style, {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    border: "2px solid #ffffff",
    backgroundColor: color,
    boxShadow: "0 1px 4px rgba(15, 23, 42, 0.45)",
    cursor: "pointer",
    padding: "0",
  });
  return element;
};

const createAssetPopupContent = (name, lifecycle) => {
  const container = document.createElement("div");
  const title = document.createElement("div");
  const detail = document.createElement("div");
  title.textContent = name || "Asset";
  detail.textContent = lifecycle || DASH;
  title.style.fontWeight = "700";
  detail.style.marginTop = "2px";
  detail.style.color = "#475569";
  container.append(title, detail);
  return container;
};

const ASSET_MAP_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256
    },
  },
  layers: [
    {
      id: "osm",
      type: "raster",
      source: "osm",
    },
  ],
};

function AssetLocationMap({ data }) {
  const mapContainerRef = useRef(null);
  const coordinates = getCoordinatePair(data);
  const latitude = coordinates?.latitude;
  const longitude = coordinates?.longitude;
  const assetName = data?.name || data?.asset_name || data?.resource_reserve_name;
  const lifecycleLabel = getLifecycleLabel(data);

  useEffect(() => {
    if (!mapContainerRef.current || latitude === undefined || longitude === undefined) return undefined;
    let map = null;
    let marker = null;
    let isMounted = true;

    import("maplibre-gl").then(
      ({ AttributionControl, Map: MapLibreMap, Marker, NavigationControl, Popup, ScaleControl }) => {
        if (!isMounted || !mapContainerRef.current) return;
        map = new MapLibreMap({
          container: mapContainerRef.current,
          style: ASSET_MAP_STYLE,
          center: [longitude, latitude],
          zoom: 4,
          minZoom: 2,
          maxZoom: 10,
          attributionControl: false,
          scrollZoom: false,
          dragPan: true,
          pitchWithRotate: false,
          dragRotate: false,
        });

        map.addControl(
          new NavigationControl({
            showCompass: false,
            visualizePitch: false,
          }),
          "top-right"
        );
        map.addControl(new ScaleControl({ maxWidth: 100, unit: "metric" }), "bottom-left");
        map.addControl(new AttributionControl({ compact: true }), "bottom-right");

        const markerElement = createCircleMarkerElement("#1f2937");
        const popup = new Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 14,
        }).setDOMContent(
          createAssetPopupContent(
            assetName,
            lifecycleLabel
          )
        );
        markerElement.addEventListener("mouseenter", () => {
          popup.setLngLat([longitude, latitude]).addTo(map);
        });
        markerElement.addEventListener("mouseleave", () => {
          popup.remove();
        });
        marker = new Marker({ element: markerElement })
          .setLngLat([longitude, latitude])
          .addTo(map);
      }
    );

    return () => {
      isMounted = false;
      marker?.getElement?.().dispatchEvent(new Event("mouseleave"));
      marker?.remove();
      map?.remove();
    };
  }, [assetName, latitude, lifecycleLabel, longitude]);

  if (!coordinates) {
    return (
      <Box
        sx={{
          height: 200,
          borderRadius: 1,
          border: "1px solid rgba(0,0,0,0.08)",
          backgroundColor: "rgba(15, 23, 42, 0.04)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Map unavailable
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={mapContainerRef}
      sx={{
        height: 200,
        borderRadius: 1,
        border: "1px solid rgba(0,0,0,0.08)",
        overflow: "hidden",
        "& .maplibregl-ctrl-group": {
          boxShadow: "0 1px 4px rgba(15, 23, 42, 0.18)",
        },
      }}
    />
  );
}

export default function AssetResourceDetailDrawer({
  open,
  onClose,
  type,
  data,
  showResourceReportInfo = true,
}) {
  if (!data) return null;
  const title = data.name || data.asset_name || data.resource_reserve_name || "Detail";
  const resourceSource = getResourceSourceReport(data);
  const lifecycleCode = data.lifecycle_code || data.lifecycle;
  const googleMapsUrl = getGoogleMapsUrl(data);
  const recentDrillResults = Array.isArray(data.recent_drill_results)
    ? data.recent_drill_results
    : [];
  const hasMiningDistrict = Boolean(data.mining_district);
  const hasLandSize = data.land_size_acres !== null && data.land_size_acres !== undefined && data.land_size_acres !== "";
  const landExplored = formatPercent(data.pct_land_explored, 2);
  const hasLandExplored = landExplored && landExplored !== DASH;
  const hasAssetDetails = hasLandSize || hasLandExplored;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: "100%", sm: 480 }, overflow: "hidden" } } }}
      ModalProps={{
        BackdropProps: {
          sx: { backgroundColor: "rgba(0,0,0,0.25)" },
        },
      }}
    >
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {title}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {type === "asset" && (
          <Stack spacing={1.25} flex={1} sx={{ overflowY: "auto", minHeight: 0, pr: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
              Asset Summary
            </Typography>
            <Divider sx={{ my: 2 }} />
            <InfoRow label="Jurisdiction" value={formatJurisdictionLabel(data.jurisdiction)} />
            <InfoRow label="Ownership" value={formatOwnership(data)} />
            {data.asset_page_url ? (
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Asset Page URL
                </Typography>
                <Button
                  href={data.asset_page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  variant="text"
                  endIcon={<OpenInNewIcon fontSize="inherit" />}
                  sx={{ minWidth: 0, p: 0 }}
                >
                  Asset Page
                </Button>
              </Box>
            ) : null}
            <InfoRow label="Lifecycle" value={data.lifecycle || DASH} />
            <InfoRow label="Lifecycle Stage" value={data.lifecycle_stage || data.stage || DASH} />
            <InfoRow
              label="Expected First Production Year"
              value={data.expected_first_production_year || DASH}
            />

            <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
              Location
            </Typography>
            <Divider sx={{ my: 2 }} />
            <AssetLocationMap data={data} />
            {googleMapsUrl ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  "&": { marginTop: "4px !important" },
                }}
              >
                <Button
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  variant="text"
                  sx={{ px: 0, fontWeight: 700 }}
                >
                  Open in Google Maps
                </Button>
              </Box>
            ) : null}
            <InfoRow label="Geological Location" value={data.geological_region || DASH} />
            {hasMiningDistrict ? (
              <InfoRow label="Mining District" value={data.mining_district} />
            ) : null}

            {hasAssetDetails ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                  Asset Details
                </Typography>
                <Divider sx={{ my: 2 }} />
                {hasLandSize ? (
                  <InfoRow
                    label="Land Size (acres)"
                    value={formatNumberWithCommas(data.land_size_acres)}
                  />
                ) : null}
                {hasLandExplored ? (
                  <InfoRow label="Land Explored" value={landExplored} />
                ) : null}
                {lifecycleCode === "production" && (
                  <InfoRow
                    label="Start Year of Operation"
                    value={data.start_year_of_operation || DASH}
                  />
                )}
                {lifecycleCode === "closure" && (
                  <InfoRow
                    label="Closure/Reclamation Year"
                    value={data.closure_or_reclamation_year || DASH}
                  />
                )}
              </>
            ) : null}

            {recentDrillResults.length ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                  Drill Results
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={0}>
                  {recentDrillResults.map((result) => (
                    <DrillResultSummary key={result.id || `${result.result_date}-${result.source_url}`} result={result} />
                  ))}
                </Stack>
              </>
            ) : null}
          </Stack>
        )}

        {type === "resource" && (
          <Stack spacing={1.25} flex={1} sx={{ overflowY: "auto", minHeight: 0, pr: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
              Resource Summary
            </Typography>
            <Divider sx={{ my: 2 }} />
            <InfoRow label="Lifecycle" value={data.lifecycle || DASH} />
            <InfoRow label="Lifecycle Stage" value={data.lifecycle_stage || DASH} />
            <InfoRow label="Mining Method" value={formatMiningMethod(data.mining_method)} />
            {["exploration", "development"].includes(lifecycleCode) && (
              <InfoRow
                label="Expected First Production Year"
                value={data.expected_first_production_year || DASH}
              />
            )}
            {lifecycleCode === "production" && (
              <InfoRow
                label="Start Year of Operation"
                value={data.start_year_of_operation || DASH}
              />
            )}
            {lifecycleCode === "closure" && (
              <InfoRow
                label="Closure/Reclamation Year"
                value={data.closure_or_reclamation_year || DASH}
              />
            )}

            {showResourceReportInfo && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                  Most Recent Resource Estimate Report Information
                </Typography>
                <Divider sx={{ my: 2 }} />
                <InfoRow label="As of Date" value={formatAsOfDate(getResourceAsOfDate(data))} />
                <InfoRow
                  label="Record Last Updated"
                  value={formatAsOfDate(getResourceLastUpdatedDate(data))}
                />
                <InfoRow
                  label="Source Report"
                  value={resourceSource?.source_title || resourceSource?.title || DASH}
                />
                <InfoRow
                  label="Source URL"
                  value={resourceSource?.source_url || resourceSource?.url || DASH}
                  href={resourceSource?.source_url || resourceSource?.url}
                />
                {data.notes && <InfoRow label="Notes" value={data.notes} />}
              </>
            )}
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />

        <Button fullWidth variant="contained" onClick={onClose}>
          Close
        </Button>
      </Box>
    </Drawer>
  );
}
